import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useDataProvider, Title, useRedirect } from 'react-admin';
import {
  Box,
  Paper,
  TextField,
  IconButton,
  Avatar,
  Typography,
  Badge,
  List,
  ListItemButton,
  ListItemAvatar,
  ListItemText,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
} from '@mui/material';
import { Send as SendIcon } from '@mui/icons-material';

// ---------------------------------------------------------------------------
// Backend এর field name (snake_case)। backend বদলালে শুধু এই object আপডেট করুন।
// ---------------------------------------------------------------------------
const FIELD = {
  id: 'id',
  user: 'user',
  username: 'username',
  text: 'text',
  isFromStaff: 'is_from_staff',
  isRead: 'is_read',
  createdAt: 'created_at',
  image: 'image',
  userAvatar: 'avatar_url',
};

const RESOURCE = 'chat-messages';
const POLL_INTERVAL_MS = 5000;
const PER_PAGE = 100; // backend এর page size limit এর সমান বা কম রাখুন

function formatTime(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  if (sameDay) {
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }
  return d.toLocaleDateString([], { day: '2-digit', month: 'short' }) +
    ' ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function initials(name) {
  if (!name) return '?';
  return name.trim().charAt(0).toUpperCase();
}

export const ChatMessagesList = () => {
  const dataProvider = useDataProvider();
  const redirect = useRedirect();
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [filterMode, setFilterMode] = useState('all'); // 'all' | 'unread'
  const [replyText, setReplyText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef(null);
  const pollRef = useRef(null);

  // ---- সব page ধরে ধরে সব মেসেজ লোড করা (ফিক্স) ----
  const fetchMessages = useCallback(async () => {
    try {
      let page = 1;
      let all = [];
      const seen = new Set();

      while (page <= 200) {
        const { data, total } = await dataProvider.getList(RESOURCE, {
          pagination: { page, perPage: PER_PAGE },
          sort: { field: FIELD.createdAt, order: 'ASC' },
          filter: {},
        });
        if (!data || data.length === 0) break;

        // নতুন কোনো মেসেজ না এলে থামো (সার্ভার pagination না মানলে অসীম লুপ এড়াতে)
        const fresh = data.filter((m) => !seen.has(m[FIELD.id]));
        if (fresh.length === 0) break;
        fresh.forEach((m) => seen.add(m[FIELD.id]));
        all = all.concat(fresh);

        if (typeof total === 'number' && all.length >= total) break;
        page += 1;
      }

      setMessages(all);
    } catch (err) {
      console.error('chat-messages load error:', err);
    } finally {
      setLoading(false);
    }
  }, [dataProvider]);

  useEffect(() => {
    fetchMessages();
    pollRef.current = setInterval(fetchMessages, POLL_INTERVAL_MS);
    return () => clearInterval(pollRef.current);
  }, [fetchMessages]);

  // ---- Group messages into conversations by user ----
  const conversations = useMemo(() => {
    const map = new Map();
    for (const m of messages) {
      const uid = m[FIELD.user];
      if (uid === undefined || uid === null) continue;
      if (!map.has(uid)) {
        map.set(uid, {
          userId: uid,
          username: m[FIELD.username] || `User ${uid}`,
          avatar: m[FIELD.userAvatar] || null,
          messages: [],
        });
      }
      map.get(uid).messages.push(m);
    }
    const list = Array.from(map.values()).map((conv) => {
      const sorted = [...conv.messages].sort(
        (a, b) => new Date(a[FIELD.createdAt]) - new Date(b[FIELD.createdAt])
      );
      const last = sorted[sorted.length - 1];
      const unreadCount = sorted.filter(
        (m) => !m[FIELD.isFromStaff] && !m[FIELD.isRead]
      ).length;
      return {
        ...conv,
        messages: sorted,
        lastMessage: last,
        unreadCount,
      };
    });
    list.sort(
      (a, b) =>
        new Date(b.lastMessage?.[FIELD.createdAt] || 0) -
        new Date(a.lastMessage?.[FIELD.createdAt] || 0)
    );
    return list;
  }, [messages]);

  const visibleConversations = useMemo(() => {
    if (filterMode === 'unread') {
      return conversations.filter((c) => c.unreadCount > 0);
    }
    return conversations;
  }, [conversations, filterMode]);

  const selectedConversation = useMemo(
    () => conversations.find((c) => c.userId === selectedUserId) || null,
    [conversations, selectedUserId]
  );

  // auto-scroll to bottom on new message / conversation switch
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [selectedConversation?.messages?.length, selectedUserId]);

  // mark unread (user-sent) messages as read when a conversation is opened
  useEffect(() => {
    if (!selectedConversation) return;
    const unread = selectedConversation.messages.filter(
      (m) => !m[FIELD.isFromStaff] && !m[FIELD.isRead]
    );
    if (unread.length === 0) return;

    (async () => {
      try {
        await Promise.all(
          unread.map((m) =>
            dataProvider.update(RESOURCE, {
              id: m[FIELD.id],
              data: { [FIELD.isRead]: true },
              previousData: m,
            })
          )
        );
        setMessages((prev) =>
          prev.map((m) =>
            unread.some((u) => u[FIELD.id] === m[FIELD.id])
              ? { ...m, [FIELD.isRead]: true }
              : m
          )
        );
      } catch (err) {
        console.error('mark as read error:', err);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId]);

  const handleSend = async () => {
    const text = replyText.trim();
    if (!text || !selectedUserId || sending) return;
    setSending(true);
    try {
      const { data: created } = await dataProvider.create(RESOURCE, {
        data: {
          [FIELD.user]: selectedUserId,
          [FIELD.text]: text,
          [FIELD.isFromStaff]: true,
        },
      });
      setMessages((prev) => [...prev, created]);
      setReplyText('');
    } catch (err) {
      console.error('send reply error:', err);
      alert('মেসেজ পাঠাতে সমস্যা হয়েছে। Console এ error details দেখুন।');
    } finally {
      setSending(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Title title="Chat Messages" />
      <Paper
        elevation={1}
        sx={{
          display: 'flex',
          height: 'calc(100vh - 180px)',
          minHeight: 500,
          overflow: 'hidden',
          mt: 1,
        }}
      >
        {/* ---------- বাম পাশ: conversation list ---------- */}
        <Box
          sx={{
            width: 320,
            flexShrink: 0,
            borderRight: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box sx={{ p: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
            <ToggleButtonGroup
              size="small"
              exclusive
              value={filterMode}
              onChange={(e, val) => val && setFilterMode(val)}
              fullWidth
            >
              <ToggleButton value="all">All</ToggleButton>
              <ToggleButton value="unread">
                Unread
                {conversations.some((c) => c.unreadCount > 0) && (
                  <Badge
                    sx={{ ml: 1 }}
                    color="error"
                    badgeContent={conversations.filter((c) => c.unreadCount > 0).length}
                  />
                )}
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <List sx={{ overflowY: 'auto', flex: 1, py: 0 }}>
            {visibleConversations.length === 0 && (
              <Box sx={{ p: 3, textAlign: 'center', color: 'text.secondary' }}>
                <Typography variant="h4" sx={{ mb: 1, opacity: 0.5 }}>💬</Typography>
                <Typography variant="body2">কোনো কথোপকথন নেই</Typography>
              </Box>
            )}
            {visibleConversations.map((conv) => (
              <React.Fragment key={conv.userId}>
                <ListItemButton
                  selected={conv.userId === selectedUserId}
                  onClick={() => setSelectedUserId(conv.userId)}
                  alignItems="flex-start"
                >
                  <ListItemAvatar>
                    <Badge
                      color="error"
                      badgeContent={conv.unreadCount}
                      overlap="circular"
                      invisible={conv.unreadCount === 0}
                    >
                      <Avatar src={conv.avatar || undefined}>
                        {!conv.avatar && initials(conv.username)}
                      </Avatar>
                    </Badge>
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Box display="flex" justifyContent="space-between">
                        <Typography
                          variant="body2"
                          fontWeight={conv.unreadCount > 0 ? 700 : 500}
                          noWrap
                          onClick={(e) => {
                            e.stopPropagation();
                            redirect('show', 'users', conv.userId);
                          }}
                          sx={{
                            cursor: 'pointer',
                            color: 'success.light',
                            textDecoration: 'underline',
                            '&:hover': { color: 'success.main' },
                          }}
                        >
                          {conv.username}
                        </Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
                          {formatTime(conv.lastMessage?.[FIELD.createdAt])}
                        </Typography>
                      </Box>
                    }
                    secondary={
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        noWrap
                        fontWeight={conv.unreadCount > 0 ? 600 : 400}
                      >
                        {conv.lastMessage?.[FIELD.isFromStaff] ? 'আপনি: ' : ''}
                        {conv.lastMessage?.[FIELD.text]}
                      </Typography>
                    }
                  />
                </ListItemButton>
                <Divider component="li" />
              </React.Fragment>
            ))}
          </List>
        </Box>

        {/* ---------- ডান পাশ: chat view ---------- */}
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
          {!selectedConversation ? (
            <Box
              flex={1}
              display="flex"
              alignItems="center"
              justifyContent="center"
              color="text.secondary"
            >
              <Typography variant="body1">চ্যাট দেখতে বামের লিস্ট থেকে একজন ইউজার সিলেক্ট করুন</Typography>
            </Box>
          ) : (
            <>
              <Box
                sx={{
                  p: 2,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                }}
              >
                <Avatar src={selectedConversation.avatar || undefined}>
                  {!selectedConversation.avatar && initials(selectedConversation.username)}
                </Avatar>
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  onClick={() => redirect('show', 'users', selectedConversation.userId)}
                  sx={{
                    cursor: 'pointer',
                    color: 'success.light',
                    textDecoration: 'underline',
                    '&:hover': { color: 'success.main' },
                  }}
                >
                  {selectedConversation.username}
                </Typography>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  overflowY: 'auto',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                  bgcolor: (theme) =>
                    theme.palette.mode === 'dark' ? 'grey.900' : 'grey.100',
                }}
              >
                {selectedConversation.messages.map((m) => {
                  const fromStaff = !!m[FIELD.isFromStaff];
                  return (
                    <Box
                      key={m[FIELD.id]}
                      sx={{
                        alignSelf: fromStaff ? 'flex-end' : 'flex-start',
                        maxWidth: '70%',
                      }}
                    >
                      <Paper
                        elevation={0}
                        sx={{
                          px: 1.5,
                          py: 1,
                          bgcolor: fromStaff ? 'primary.main' : 'background.paper',
                          color: fromStaff ? 'primary.contrastText' : 'text.primary',
                          borderRadius: 2,
                          borderTopRightRadius: fromStaff ? 4 : 16,
                          borderTopLeftRadius: fromStaff ? 16 : 4,
                        }}
                      >
                        {FIELD.image && m[FIELD.image] && (
                          <Box
                            component="img"
                            src={m[FIELD.image]}
                            alt="attachment"
                            sx={{ maxWidth: '100%', borderRadius: 1, mb: m[FIELD.text] ? 1 : 0 }}
                          />
                        )}
                        {m[FIELD.text] && (
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {m[FIELD.text]}
                          </Typography>
                        )}
                      </Paper>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', textAlign: fromStaff ? 'right' : 'left', mt: 0.3 }}
                      >
                        {formatTime(m[FIELD.createdAt])}
                      </Typography>
                    </Box>
                  );
                })}
                <div ref={bottomRef} />
              </Box>

              <Box
                sx={{
                  p: 1.5,
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  display: 'flex',
                  gap: 1,
                  alignItems: 'flex-end',
                }}
              >
                <TextField
                  fullWidth
                  size="small"
                  multiline
                  maxRows={4}
                  placeholder="রিপ্লাই লিখুন..."
                  value={replyText}
                  onChange={(e) => setReplyText(e.target.value)}
                  onKeyDown={handleKeyDown}
                  disabled={sending}
                />
                <IconButton
                  color="primary"
                  onClick={handleSend}
                  disabled={sending || !replyText.trim()}
                >
                  {sending ? <CircularProgress size={20} /> : <SendIcon />}
                </IconButton>
              </Box>
            </>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ChatMessagesList;